import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Layers, MapPin, Phone, Users, Save, ScrollText, ArrowRight, MessageCircle, AlertTriangle, Settings2, X, Plus } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const NICHO_LABEL = { moveis: "Móveis", estofados: "Estofados", colchoes: "Colchões" };

function CardResumoDiario() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/empresa/config-resumo").then(r => setCfg(r.data)).catch(() => {});
  }, []);
  const salvar = async (novo) => {
    try {
      const r = await api.post("/empresa/config-resumo", novo);
      setCfg(r.data);
      toast.success(r.data.ativo ? `Resumo diário ativo às ${r.data.hora}` : "Resumo diário desligado");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!cfg) return null;
  return (
    <div className="aj-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="me-card-resumo">
      <div className="flex items-start gap-3">
        <div className="aj-icon-tile"><MessageCircle size={20} /></div>
        <div>
          <div className="font-display font-bold text-aj-blue-deep">Resumo diário por WhatsApp</div>
          <p className="text-xs text-aj-text-muted mt-0.5 max-w-lg">
            Agenda do dia e riscos em aberto, enviados todas as manhãs no horário escolhido.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <select value={cfg.hora} onChange={e => salvar({ ...cfg, hora: e.target.value })}
          disabled={!cfg.ativo}
          className="border border-border rounded-lg px-2 py-1.5 text-sm bg-white disabled:opacity-50"
          data-testid="me-resumo-hora">
          {Array.from({ length: 17 }, (_, i) => `${String(i + 5).padStart(2, "0")}:00`).map(h => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <Switch checked={cfg.ativo} onCheckedChange={v => salvar({ ...cfg, ativo: v })} data-testid="me-resumo-toggle" />
      </div>
    </div>
  );
}

function SimNao({ label, valor, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
      <span className="text-sm">{label}</span>
      <div className="flex gap-1.5">
        <button onClick={() => onChange(true)}
          className={`px-3 py-1 rounded text-xs font-bold ${valor === true ? "bg-[#00A859] text-white" : "border border-border text-aj-text-muted hover:border-[#00A859]"}`}
          data-testid={`${testid}-sim`}>SIM</button>
        <button onClick={() => onChange(false)}
          className={`px-3 py-1 rounded text-xs font-bold ${valor === false ? "bg-[#E60000] text-white" : "border border-border text-aj-text-muted hover:border-[#E60000]"}`}
          data-testid={`${testid}-nao`}>NÃO</button>
      </div>
    </div>
  );
}

export default function MinhaEmpresa() {
  const [emp, setEmp] = useState(null);
  const [catalogo, setCatalogo] = useState({ nichos: [], servicos_por_nicho: {} });
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [municipios, setMunicipios] = useState({}); // {UF: [cidades]}
  const [buscaCidade, setBuscaCidade] = useState({}); // {UF: texto}
  const [bairrosDisp, setBairrosDisp] = useState({}); // {"UF|cidade": {loading, lista, fonte}}
  const [buscaBairro, setBuscaBairro] = useState({}); // {"UF|cidade": texto}
  const [novoServico, setNovoServico] = useState({}); // {nicho: texto}

  const loadOnboarding = () => api.get("/empresa/onboarding-status").then(r => setOnboarding(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/minha-empresa").then(r => {
      setEmp(r.data);
      setForm({
        nome_fantasia: r.data.nome_fantasia || "",
        endereco: r.data.endereco || "", bairro: r.data.bairro || "",
        cidade: r.data.cidade || "", estado: r.data.estado || "",
        telefone_comercial: r.data.telefone_comercial || "",
        telefone_financeiro: r.data.telefone_financeiro || "",
        telefone_operacional: r.data.telefone_operacional || "",
        email_administrativo: r.data.email_administrativo || "",
        email_financeiro: r.data.email_financeiro || "",
        responsavel_legal: r.data.responsavel_legal || "",
        responsavel_operacional: r.data.responsavel_operacional || "",
        responsavel_financeiro: r.data.responsavel_financeiro || "",
        nichos: r.data.nichos || [],
        servicos_oferecidos: r.data.servicos_oferecidos || [],
        servicos_personalizados: r.data.servicos_personalizados || {},
        areas_atuacao: r.data.areas_atuacao || [],
        atende_brasil: !!r.data.atende_brasil,
        usa_montadora: r.data.usa_montadora ?? null,
        aceita_montador_sem_cnpj: r.data.aceita_montador_sem_cnpj ?? null,
        exige_nota_fiscal: r.data.exige_nota_fiscal ?? null,
      });
    }).catch(e => toast.error(formatApiError(e)));
    api.get("/catalogo/nichos").then(r => setCatalogo(r.data)).catch(() => {});
    loadOnboarding();
  }, []);

  const carregarMunicipios = (uf) => {
    if (municipios[uf]) return;
    api.get(`/localidades/${uf}/municipios`)
      .then(r => setMunicipios(m => ({ ...m, [uf]: r.data })))
      .catch(() => toast.error(`Não foi possível carregar as cidades de ${uf}`));
  };

  const carregarBairros = (uf, cidade) => {
    const key = `${uf}|${cidade}`;
    if (bairrosDisp[key]) return;
    setBairrosDisp(b => ({ ...b, [key]: { loading: true, lista: [] } }));
    api.get(`/localidades/${uf}/${encodeURIComponent(cidade)}/bairros`)
      .then(r => setBairrosDisp(b => ({ ...b, [key]: { loading: false, lista: r.data.bairros || [], fonte: r.data.fonte } })))
      .catch(() => setBairrosDisp(b => ({ ...b, [key]: { loading: false, lista: [] } })));
  };

  const toggleEstado = (uf) => {
    setForm(f => {
      const existe = f.areas_atuacao.some(a => a.estado === uf);
      if (existe) return { ...f, areas_atuacao: f.areas_atuacao.filter(a => a.estado !== uf) };
      carregarMunicipios(uf);
      return { ...f, areas_atuacao: [...f.areas_atuacao, { estado: uf, todas_cidades: true, cidades: [] }] };
    });
  };

  const updateArea = (uf, patch) => {
    setForm(f => ({ ...f, areas_atuacao: f.areas_atuacao.map(a => a.estado === uf ? { ...a, ...patch } : a) }));
  };

  const addCidade = (uf, nome) => {
    if (!nome) return;
    carregarBairros(uf, nome);
    setForm(f => ({
      ...f,
      areas_atuacao: f.areas_atuacao.map(a => {
        if (a.estado !== uf) return a;
        if (a.cidades.some(c => c.nome === nome)) return a;
        return { ...a, todas_cidades: false, cidades: [...a.cidades, { nome, bairros: [] }] };
      }),
    }));
    setBuscaCidade(b => ({ ...b, [uf]: "" }));
  };

  const salvarDecisao = async (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }));
    try {
      await api.put("/minha-empresa", { [campo]: valor });
      toast.success("Preferência salva");
      loadOnboarding();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const estados = form.atende_brasil ? UFS : form.areas_atuacao.map(a => a.estado);
      const cidades = form.areas_atuacao.flatMap(a => a.cidades.map(c => c.nome));
      await api.put("/minha-empresa", {
        ...form,
        email_administrativo: form.email_administrativo || null,
        email_financeiro: form.email_financeiro || null,
        estados_atendidos: estados,
        cidades_atendidas: cidades,
      });
      toast.success("Dados da empresa atualizados");
      loadOnboarding();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  if (!form) return <div className="p-12 text-center text-aj-text-muted">Carregando…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Configurações · Empresa</div>
        <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Minha Empresa</h1>
        <p className="text-sm text-aj-text-muted mt-1">
          {emp?.razao_social || emp?.nome} · CNPJ {emp?.cnpj} · Sigla OS <span className="font-mono font-bold text-aj-blue-dark">{emp?.sigla_os}</span>
        </p>
      </div>

      {onboarding && !onboarding.completo && (
        <div className="rounded-lg border border-[#FFCC00] bg-[#FFFBEB] p-4" data-testid="me-onboarding-pendencias">
          <div className="flex items-center gap-2 font-bold text-sm text-[#92400E]">
            <AlertTriangle size={16} /> Complete o cadastro antes de importar planilhas
          </div>
          <ul className="mt-2 space-y-1">
            {onboarding.pendencias.map(p => (
              <li key={p.key} className="text-xs text-[#92400E]">
                • {p.label}{p.rota !== "/admin/minha-empresa" && (
                  <Link to={p.rota} className="ml-1 underline font-bold">abrir</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Decisões operacionais obrigatórias */}
      <div className="aj-card p-6" data-testid="me-card-preferencias">
        <SectionTitle icon={<Settings2 size={14} />}>Preferências operacionais (obrigatório)</SectionTitle>
        <p className="text-[11px] text-aj-text-muted mt-1">Salvamento automático ao clicar em Sim/Não.</p>
        <div className="mt-2">
          <SimNao label="Trabalha com Montadora (gestora de montadores)?" valor={form.usa_montadora}
            onChange={v => salvarDecisao("usa_montadora", v)} testid="me-usa-montadora" />
          <SimNao label="Aceita montador SEM CNPJ (autônomo pessoa física)?" valor={form.aceita_montador_sem_cnpj}
            onChange={v => salvarDecisao("aceita_montador_sem_cnpj", v)} testid="me-sem-cnpj" />
          <SimNao label="Exige emissão de nota fiscal do montador?" valor={form.exige_nota_fiscal}
            onChange={v => salvarDecisao("exige_nota_fiscal", v)} testid="me-nota-fiscal" />
        </div>
      </div>

      {/* Valores: aponta para a Tabela oficial */}
      <div className="aj-card p-5 border-l-4 border-l-[#FF6700] flex flex-wrap items-center justify-between gap-3" data-testid="me-card-valores">
        <div className="flex items-start gap-3">
          <div className="aj-icon-tile"><ScrollText size={20} /></div>
          <div>
            <div className="font-display font-bold text-aj-blue-deep">Valores pagos aos montadores</div>
            <p className="text-xs text-aj-text-muted mt-0.5 max-w-lg">
              São definidos na <strong>Tabela de Valores</strong> oficial (alterações entram em vigor em D+60, com comunicado automático aos montadores).
            </p>
          </div>
        </div>
        <Link to="/admin/valores-servicos" className="aj-link-orange text-sm" data-testid="me-link-tabela">
          Abrir Tabela de Valores <ArrowRight size={15} />
        </Link>
      </div>

      <CardResumoDiario />

      {/* Nichos & Serviços — listas separadas por nicho */}
      <div className="aj-card p-6" data-testid="me-card-nichos">
        <SectionTitle icon={<Layers size={14} />}>Nichos & Serviços</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
          {(catalogo.nichos || []).map(n => (
            <label key={n}
              className={`rounded-lg border p-3 cursor-pointer text-sm font-medium transition-colors ${
                form.nichos.includes(n)
                  ? "bg-aj-blue-dark text-white border-transparent"
                  : "border-aj-border hover:border-aj-blue-dark text-aj-text"
              }`}
              data-testid={`me-nicho-${n}`}>
              <input type="checkbox" checked={form.nichos.includes(n)}
                onChange={(e) => setForm(f => ({
                  ...f, nichos: e.target.checked ? [...f.nichos, n] : f.nichos.filter(x => x !== n)
                }))} className="mr-2 accent-white" />
              {NICHO_LABEL[n] || n}
            </label>
          ))}
        </div>

        {form.nichos.map(n => {
          const doCatalogo = catalogo.servicos_por_nicho[n] || [];
          const personalizados = form.servicos_personalizados[n] || [];
          const servicos = [...doCatalogo, ...personalizados];
          return (
            <div key={n} className="mt-5 border border-border rounded-lg p-4" data-testid={`me-nicho-servicos-${n}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-aj-blue-dark">{NICHO_LABEL[n] || n}</div>
                <button className="text-[11px] underline text-aj-text-muted"
                  onClick={() => setForm(f => ({
                    ...f,
                    servicos_oferecidos: [...new Set([...f.servicos_oferecidos, ...servicos])],
                  }))} data-testid={`me-srv-todos-${n}`}>
                  Selecionar todos
                </button>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {servicos.map(s => (
                  <label key={s} className="flex items-center gap-2 text-xs rounded-lg border border-aj-border p-2.5 cursor-pointer hover:bg-aj-surface transition-colors">
                    <input type="checkbox" checked={form.servicos_oferecidos.includes(s)}
                      onChange={(e) => setForm(f => ({
                        ...f, servicos_oferecidos: e.target.checked ? [...f.servicos_oferecidos, s] : f.servicos_oferecidos.filter(x => x !== s)
                      }))}
                      data-testid={`me-srv-${s}`} />
                    <span className="capitalize">{s.replace(/_/g, " ")}</span>
                    {personalizados.includes(s) && (
                      <button className="ml-auto text-[#E60000]" onClick={(ev) => { ev.preventDefault();
                        setForm(f => ({ ...f,
                          servicos_personalizados: { ...f.servicos_personalizados, [n]: (f.servicos_personalizados[n] || []).filter(x => x !== s) },
                          servicos_oferecidos: f.servicos_oferecidos.filter(x => x !== s),
                        })); }}>
                        <X size={12} />
                      </button>
                    )}
                  </label>
                ))}
              </div>
              <div className="mt-3 flex gap-2 max-w-md">
                <Input value={novoServico[n] || ""} onChange={e => setNovoServico(v => ({ ...v, [n]: e.target.value }))}
                  placeholder="Cadastrar novo serviço neste nicho" className="h-8 text-xs" data-testid={`me-srv-novo-${n}`} />
                <Button size="sm" variant="outline" className="h-8" data-testid={`me-srv-add-${n}`}
                  onClick={() => {
                    const key = (novoServico[n] || "").trim().toLowerCase().replace(/\s+/g, "_");
                    if (!key) return;
                    setForm(f => ({ ...f,
                      servicos_personalizados: { ...f.servicos_personalizados, [n]: [...new Set([...(f.servicos_personalizados[n] || []), key])] },
                      servicos_oferecidos: [...new Set([...f.servicos_oferecidos, key])],
                    }));
                    setNovoServico(v => ({ ...v, [n]: "" }));
                  }}>
                  <Plus size={14} /> Adicionar
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Área de atuação — País > Estado > Cidades > Bairros */}
      <div className="aj-card p-6" data-testid="me-card-areas">
        <SectionTitle icon={<MapPin size={14} />}>Área de atuação</SectionTitle>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm"><strong>País:</strong> Brasil</div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.atende_brasil}
              onChange={e => setForm(f => ({ ...f, atende_brasil: e.target.checked }))}
              data-testid="me-atende-brasil" />
            Atendo o Brasil inteiro (todos os estados)
          </label>
        </div>

        {!form.atende_brasil && (
          <>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-aj-text-muted">Estados</div>
              <button className="text-[11px] underline text-aj-text-muted" data-testid="me-uf-todos"
                onClick={() => setForm(f => ({
                  ...f,
                  areas_atuacao: UFS.map(uf => f.areas_atuacao.find(a => a.estado === uf) || { estado: uf, todas_cidades: true, cidades: [] }),
                }))}>
                Selecionar todos
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {UFS.map(uf => {
                const on = form.areas_atuacao.some(a => a.estado === uf);
                return (
                  <button key={uf} onClick={() => toggleEstado(uf)}
                    className={`px-2.5 py-1 rounded text-xs font-bold ${on ? "bg-aj-blue-dark text-white" : "border border-border text-aj-text-muted hover:border-aj-blue-dark"}`}
                    data-testid={`me-uf-${uf}`}>
                    {uf}
                  </button>
                );
              })}
            </div>

            {form.areas_atuacao.map(a => (
              <div key={a.estado} className="mt-4 border border-border rounded-lg p-3" data-testid={`me-area-${a.estado}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-aj-blue-dark">{a.estado}</div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={a.todas_cidades}
                      onChange={e => updateArea(a.estado, { todas_cidades: e.target.checked, cidades: e.target.checked ? [] : a.cidades })}
                      data-testid={`me-area-todas-${a.estado}`} />
                    Todas as cidades do estado
                  </label>
                </div>
                {!a.todas_cidades && (
                  <div className="mt-2">
                    <div className="flex gap-2 max-w-md">
                      <Input list={`cidades-${a.estado}`} value={buscaCidade[a.estado] || ""}
                        onChange={e => setBuscaCidade(b => ({ ...b, [a.estado]: e.target.value }))}
                        onFocus={() => carregarMunicipios(a.estado)}
                        placeholder="Buscar cidade…" className="h-8 text-xs" data-testid={`me-area-busca-${a.estado}`} />
                      <datalist id={`cidades-${a.estado}`}>
                        {(municipios[a.estado] || []).map(c => <option key={c} value={c} />)}
                      </datalist>
                      <Button size="sm" variant="outline" className="h-8"
                        onClick={() => addCidade(a.estado, (buscaCidade[a.estado] || "").trim())}
                        data-testid={`me-area-add-${a.estado}`}>
                        <Plus size={14} />
                      </Button>
                    </div>
                    <div className="mt-2 space-y-2">
                      {a.cidades.map(c => (
                        <div key={c.nome} className="border border-border rounded-lg p-2.5" data-testid={`me-cidade-bloco-${c.nome}`}>
                          <div className="flex items-center justify-between gap-2 text-xs">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-aj-amber-soft font-semibold">
                              {c.nome}
                              <button onClick={() => updateArea(a.estado, { cidades: a.cidades.filter(x => x.nome !== c.nome) })}
                                className="hover:text-[#E60000]"><X size={12} /></button>
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">
                              {(c.bairros || []).length === 0 ? "Todos os bairros" : `${c.bairros.length} bairro(s) selecionado(s)`}
                            </span>
                          </div>
                          <BairrosPicker
                            uf={a.estado} cidade={c.nome}
                            selecionados={c.bairros || []}
                            disp={bairrosDisp[`${a.estado}|${c.nome}`]}
                            busca={buscaBairro[`${a.estado}|${c.nome}`] || ""}
                            setBusca={t => setBuscaBairro(b => ({ ...b, [`${a.estado}|${c.nome}`]: t }))}
                            onCarregar={() => carregarBairros(a.estado, c.nome)}
                            onChange={bairros => updateArea(a.estado, {
                              cidades: a.cidades.map(x => x.nome === c.nome ? { ...x, bairros } : x),
                            })} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Contatos */}
      <div className="aj-card p-6" data-testid="me-card-contatos">
        <SectionTitle icon={<Phone size={14} />}>Contatos</SectionTitle>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <FF label="Nome fantasia" v={form.nome_fantasia} on={(v) => setForm(f => ({...f, nome_fantasia: v}))} testid="me-fantasia" />
          <FF label="Telefone comercial *" v={form.telefone_comercial} on={(v) => setForm(f => ({...f, telefone_comercial: v}))} testid="me-tel-com" />
          <FF label="Telefone financeiro" v={form.telefone_financeiro} on={(v) => setForm(f => ({...f, telefone_financeiro: v}))} testid="me-tel-fin" />
          <FF label="Telefone operacional" v={form.telefone_operacional} on={(v) => setForm(f => ({...f, telefone_operacional: v}))} testid="me-tel-op" />
          <FF label="E-mail administrativo" v={form.email_administrativo} on={(v) => setForm(f => ({...f, email_administrativo: v}))} testid="me-email-adm" />
          <FF label="E-mail financeiro" v={form.email_financeiro} on={(v) => setForm(f => ({...f, email_financeiro: v}))} testid="me-email-fin" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <FF label="Endereço (rua, número)" v={form.endereco} on={(v) => setForm(f => ({...f, endereco: v}))} testid="me-endereco" />
          <FF label="Bairro" v={form.bairro} on={(v) => setForm(f => ({...f, bairro: v}))} testid="me-bairro" />
          <FF label="Cidade" v={form.cidade} on={(v) => setForm(f => ({...f, cidade: v}))} testid="me-cidade" />
          <FF label="Estado (UF)" v={form.estado} on={(v) => setForm(f => ({...f, estado: v}))} testid="me-estado" />
        </div>
      </div>

      {/* Responsáveis */}
      <div className="aj-card p-6" data-testid="me-card-responsaveis">
        <SectionTitle icon={<Users size={14} />}>Responsáveis</SectionTitle>
        <div className="grid md:grid-cols-3 gap-4 mt-3">
          <FF label="Legal" v={form.responsavel_legal} on={(v) => setForm(f => ({...f, responsavel_legal: v}))} testid="me-resp-legal" />
          <FF label="Operacional" v={form.responsavel_operacional} on={(v) => setForm(f => ({...f, responsavel_operacional: v}))} testid="me-resp-op" />
          <FF label="Financeiro" v={form.responsavel_financeiro} on={(v) => setForm(f => ({...f, responsavel_financeiro: v}))} testid="me-resp-fin" />
        </div>
      </div>

      <button onClick={salvar} disabled={salvando} className="btn-aj-primary h-12 w-full justify-center disabled:opacity-60" data-testid="me-salvar">
        <Save size={17} /> {salvando ? "Salvando…" : "Salvar alterações"}
      </button>
    </div>
  );
}

function BairrosPicker({ uf, cidade, selecionados, disp, busca, setBusca, onCarregar, onChange }) {
  useEffect(() => { onCarregar(); /* eslint-disable-next-line */ }, []);
  const lista = disp?.lista || [];
  const filtrados = busca
    ? lista.filter(b => b.toLowerCase().includes(busca.toLowerCase()))
    : lista;
  const toggle = (b) => {
    onChange(selecionados.includes(b) ? selecionados.filter(x => x !== b) : [...selecionados, b]);
  };
  if (disp?.loading) {
    return <div className="mt-2 text-[11px] text-aj-text-muted animate-pulse">Carregando bairros de {cidade}…</div>;
  }
  if (lista.length === 0) {
    return (
      <div className="mt-2 text-[11px] text-aj-text-muted" data-testid={`me-bairros-vazio-${cidade}`}>
        Lista de bairros indisponível para {cidade} — a cidade inteira será atendida.
      </div>
    );
  }
  return (
    <div className="mt-2" data-testid={`me-bairros-${cidade}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder={`Buscar bairro em ${cidade}…`} className="h-7 text-[11px] max-w-52"
          data-testid={`me-bairros-busca-${cidade}`} />
        <button className="text-[10px] underline text-aj-text-muted"
          onClick={() => onChange([])} data-testid={`me-bairros-todos-${cidade}`}>
          Todos os bairros
        </button>
        <button className="text-[10px] underline text-aj-text-muted"
          onClick={() => onChange(lista)} data-testid={`me-bairros-selecionar-todos-${cidade}`}>
          Selecionar todos ({lista.length})
        </button>
      </div>
      <div className="mt-2 max-h-40 overflow-auto flex flex-wrap gap-1">
        {filtrados.map(b => {
          const on = selecionados.includes(b);
          return (
            <button key={b} onClick={() => toggle(b)}
              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${on
                ? "bg-aj-blue-dark text-white"
                : "border border-border text-aj-text-muted hover:border-aj-blue-dark"}`}
              data-testid={`me-bairro-${cidade}-${b}`}>
              {b}
            </button>
          );
        })}
        {filtrados.length === 0 && <span className="text-[10px] text-aj-text-muted">Nenhum bairro encontrado para "{busca}"</span>}
      </div>
      <div className="mt-1 text-[10px] text-aj-text-muted">
        Nenhum selecionado = atende <strong>todos os bairros</strong> de {cidade}. Seleção evita erro de grafia e alimenta o ranking de regiões que mais vendem.
      </div>
    </div>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-aj-blue-dark">
      <span className="text-aj-orange">{icon}</span> {children}
    </div>
  );
}
function FF({ label, v, on, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</Label>
      <Input value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2 rounded-lg" data-testid={testid} />
    </div>
  );
}
